import React from 'react';

import cx from '../../utils/classNames';
import toCamelCase from '../../utils/toCamelCase';
import Icon from './Icon';
import LoadingCircle from './loadingCircle';

import styles from './button.module.scss';

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLProps,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react';

/** Custom props */
type BaseProps<T extends string | undefined> = {
  /** The content of the component */
  children?: ReactNode;
  /** Custom class to overwrite default styling if needed */
  className?: string;
  /** React Ref for the button element */
  innerRef?: T extends string ? Ref<HTMLAnchorElement> : Ref<HTMLButtonElement>;
  /** When true, makes the element not mutable, focusable, or submitted with the form */
  isDisabled?: boolean;
  /** If true, the loading indicator is visible and the button is disabled */
  isLoading?: boolean;
  /** Controls whether the button should be in skeleton loading */
  isSkeleton?: boolean;
  /** Calls a function when the button is clicked */
  onClick?: T extends string
    ? MouseEventHandler<HTMLAnchorElement>
    : MouseEventHandler<HTMLButtonElement>;
  /** Controls the size of the button */
  size?: 'small' | 'medium' | 'large';
  /** Define the default behavior of the button */
  type?: 'submit' | 'reset' | 'button';
  /** Controls the variant of the button */
  variant?: 'filled' | 'outlined' | 'ghost';
  /** Sets the width to either fit the content or to fit the container */
  width?: 'content' | 'full';
};

export type Props<T extends string | undefined> = T extends string
  ? BaseProps<string> & {
      /**
       * The URL to link to when the button is clicked.
       * If defined, an a element will be used as the root node.
       */
      href: T;
    } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'size' | 'ref'>
  : BaseProps<undefined> & { href?: T } & Omit<
        HTMLProps<HTMLButtonElement>,
        'href' | 'size' | 'ref' | 'type'
      >;

export function Button(props: Props<string | undefined>) {
  const {
    children,
    className,
    href,
    innerRef,
    isDisabled,
    isLoading,
    isSkeleton,
    onClick,
    size = 'medium',
    type = 'button',
    variant = 'filled',
    width = 'content',
    ...rest
  } = props;

  const classNames = cx(
    styles.button,
    styles[toCamelCase('button', size)],
    isDisabled ? styles.buttonDisabled : '',
    isSkeleton ? styles.buttonSkeleton : styles[toCamelCase('button', variant)],
    styles[toCamelCase('button', width, 'width')],
    className,
  );

  const iconLoading = (
    <Icon>
      <LoadingCircle isLoading classType="primary" />
    </Icon>
  );

  const contents = (
    <>
      {children}
      {isLoading && iconLoading}
    </>
  );

  if (isSkeleton) {
    return <div className={classNames} data-testid="button-skeleton-div" />;
  } else if (href !== undefined) {
    return (
      <a
        className={classNames}
        href={href}
        onClick={onClick}
        ref={innerRef}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {contents}
      </a>
    );
  } else {
    return (
      <button
        data-test={href}
        className={classNames}
        disabled={isDisabled}
        type={type}
        onClick={!isLoading ? onClick : undefined}
        ref={innerRef}
        {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {contents}
      </button>
    );
  }
}

export default Button;
